import { useEffect, useState } from "react";
import { Avatar } from "../components/Avatar";
import { api, ApiError, type Profile } from "../lib/api";

/* Sign in.
 *
 * Two steps, because the first one has to work for someone who cannot read:
 * pick your face, then enter your number. Parents get the same picker and a
 * password field instead of the pad.
 */
export function Login({
  onSignedIn,
  onUnreachable,
}: {
  onSignedIn: () => void;
  /** The profile list is the first thing this screen needs. If it cannot be
   *  fetched there is nothing to sign in to, so say so once, in one place,
   *  rather than rendering a picker with nobody in it. */
  onUnreachable?: () => void;
}) {
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [picked, setPicked] = useState<Profile | null>(null);
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  useEffect(() => {
    api
      .get<Profile[]>("/auth/profiles")
      .then(setProfiles)
      .catch(() => {
        // Swallowing this produced the worst screen in the app: "Who's here?"
        // over an empty grid, with no accounts, no error and no way forward.
        if (onUnreachable) onUnreachable();
        else setProfiles([]);
      });
  }, [onUnreachable]);

  async function submit(value: string) {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/login", { loginName: picked.loginName, secret: value });
      onSignedIn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
      setSecret("");
    } finally {
      setBusy(false);
    }
  }

  function press(digit: string) {
    const next = secret + digit;
    setSecret(next);
    if (next.length === 4) void submit(next);
  }

  if (profiles === null) return <Centered>Loading&hellip;</Centered>;

  // A family exists -- otherwise App would have sent us to setup -- but it has
  // nobody who can sign in. Rare, and reachable by archiving every account, so
  // it gets a real answer rather than an empty grid.
  if (profiles.length === 0) {
    return (
      <Centered>
        <h1 style={{ fontSize: 28, marginBottom: 6 }}>No accounts yet</h1>
        <p style={{ color: "var(--ink-2)", margin: 0 }}>
          This family has no one who can sign in. A grown-up needs to add an
          account before anyone can get in.
        </p>
      </Centered>
    );
  }

  if (!picked) {
    return (
      <Centered>
        <h1 style={{ fontSize: 30, marginBottom: 4 }}>Who&rsquo;s here?</h1>
        <p style={{ color: "var(--ink-2)", margin: "0 0 28px" }}>
          Tap your picture to start.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))",
            gap: 16,
            width: "100%",
          }}
        >
          {profiles.map((p) => (
            <button
              key={p.loginName}
              onClick={() => {
                setPicked(p);
                setSecret("");
                setError(null);
                setForgot(false);
              }}
              style={{
                display: "grid",
                justifyItems: "center",
                gap: 10,
                padding: "18px 8px",
                minHeight: 0,
              }}
            >
              <Avatar name={p.avatar} size={72} ring={p.color} />
              <span style={{ fontFamily: "var(--font-display)", fontSize: 17 }}>
                {p.displayName}
              </span>
            </button>
          ))}
        </div>
      </Centered>
    );
  }

  if (forgot) {
    return (
      <Centered>
        <Avatar name={picked.avatar} size={84} ring={picked.color} />
        <h1 style={{ fontSize: 26, margin: "14px 0 2px" }}>Reset password</h1>
        <ForgotPasswordForm onBack={() => setForgot(false)} />
      </Centered>
    );
  }

  return (
    <Centered>
      <Avatar name={picked.avatar} size={84} ring={picked.color} />
      <h1 style={{ fontSize: 26, margin: "14px 0 2px" }}>Hi {picked.displayName}!</h1>
      <p style={{ color: "var(--ink-2)", margin: "0 0 22px" }}>
        {picked.role === "child" ? "Type your secret number." : "Enter your password."}
      </p>

      {picked.role === "child" ? (
        <>
          <div
            aria-label={`${secret.length} of 4 digits entered`}
            style={{ display: "flex", gap: 12, marginBottom: 22 }}
          >
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  border: "3px solid var(--ink)",
                  background: i < secret.length ? "var(--ink)" : "transparent",
                }}
              />
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 78px)",
              gap: 12,
              justifyContent: "center",
            }}
          >
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <PadKey key={d} onClick={() => press(d)} disabled={busy}>
                {d}
              </PadKey>
            ))}
            <PadKey onClick={() => setSecret("")} disabled={busy} muted>
              clear
            </PadKey>
            <PadKey onClick={() => press("0")} disabled={busy}>
              0
            </PadKey>
            <PadKey onClick={() => setSecret(secret.slice(0, -1))} disabled={busy} muted>
              ←
            </PadKey>
          </div>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit(secret);
          }}
          style={{ width: "100%", maxWidth: 320, display: "grid", gap: 12 }}
        >
          <input
            type="password"
            autoFocus
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Password"
            aria-label="Password"
          />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button
            type="button"
            onClick={() => setForgot(true)}
            style={{
              background: "none",
              border: "none",
              boxShadow: "none",
              color: "var(--ink-2)",
              textDecoration: "underline",
              minHeight: 0,
              fontSize: 13.5,
              justifySelf: "center",
            }}
          >
            Forgot your password?
          </button>
        </form>
      )}

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, marginTop: 18 }}>
          {error}
        </p>
      )}

      <button
        onClick={() => {
          setPicked(null);
          setSecret("");
          setError(null);
          setForgot(false);
        }}
        style={{
          marginTop: 26,
          background: "none",
          border: "none",
          boxShadow: "none",
          color: "var(--ink-2)",
          textDecoration: "underline",
          minHeight: 0,
        }}
      >
        Not you? Go back
      </button>
    </Centered>
  );
}

/* Ask for a reset link by email.
 *
 * Answers the same way whether or not the address matches an account -- the
 * server is deliberately vague, so this is too. Anything more specific here
 * ("no account uses that email") would just move the leak from the API
 * response to the screen reading it.
 */
function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (message) {
    return (
      <>
        <p style={{ color: "var(--ink-2)", margin: "0 0 22px", maxWidth: 320 }}>
          {message}
        </p>
        <button onClick={onBack} style={{ minWidth: 160 }}>
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          const r = await api.post<{ message: string }>("/auth/password/forgot", {
            email,
          });
          setMessage(r.message);
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "Something went wrong.");
        } finally {
          setBusy(false);
        }
      }}
      style={{ width: "100%", maxWidth: 320, display: "grid", gap: 12 }}
    >
      <p style={{ color: "var(--ink-2)", margin: "0 0 4px" }}>
        Enter the email on the account and we&rsquo;ll send a link to set a
        new password.
      </p>
      <input
        type="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        aria-label="Email"
        required
      />
      <button type="submit" disabled={busy}>
        {busy ? "Sending…" : "Send reset link"}
      </button>
      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, margin: 0 }}>
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          boxShadow: "none",
          color: "var(--ink-2)",
          textDecoration: "underline",
          minHeight: 0,
          justifySelf: "center",
        }}
      >
        Back to sign in
      </button>
    </form>
  );
}

function PadKey({
  children,
  onClick,
  disabled,
  muted,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  muted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 72,
        padding: 0,
        fontFamily: muted ? "var(--font-ui)" : "var(--font-num)",
        fontSize: muted ? 14 : 26,
        fontWeight: 700,
        color: muted ? "var(--ink-2)" : "var(--ink)",
      }}
    >
      {children}
    </button>
  );
}

export function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: 460,
          width: "100%",
        }}
      >
        {children}
      </div>
    </main>
  );
}
